import {
  Agent,
  CheckoutSession,
  getLoanById,
  LenderName,
  Loan,
  PaymentIntent,
  PaymentMethodType,
} from '@nemuru-eng/js-api-client';

import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { PaymentMethodFactory } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/PaymentMethodFactory';
import { CreditLinePaymentMethod } from '@/components/modules/Application/components/PaymentMethod/CreditLine/entities/PaymentMethod/CreditLinePaymentMethod';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';

export class CreditLinePaymentMethodDetailEntity extends LoanPaymentMethodDetailEntity {
  static allowed = [PaymentMethodType.CREDIT_LINE];

  constructor(
    paymentMethodType: PaymentMethodType,
    checkoutSession: CheckoutSession,
    agent: Agent,
    paymentIntent?: PaymentIntent,
  ) {
    super(paymentMethodType, checkoutSession, agent, paymentIntent);
  }

  public static isAllowed(type: PaymentMethodType): boolean {
    return this.allowed.includes(type);
  }

  public async refresh(): Promise<this> {
    if (!this.paymentMethodId) {
      if (!this.paymentMethod) {
        this.setPaymentMethodFromCalculator();
      }
      this._isRefreshed = true;
      return this;
    }

    try {
      const loan = await getLoanById({ loanId: this.paymentMethodId });
      await this.refreshDataFromLoan(loan);
    } catch (error) {
      this.setError(error as Error);
    }

    this._isRefreshed = true;
    return this;
  }

  public setPaymentMethodFromCalculator(): void {
    const paymentMethodEntity = PaymentMethodFactory.byType(this.type);

    if (!(paymentMethodEntity instanceof CreditLinePaymentMethod)) {
      throw new Error('Payment method entity is not a credit line payment method');
    }

    const totalToFinance = this.totalToFinance();
    paymentMethodEntity.setup(totalToFinance.value, totalToFinance.currency);

    const lenderNames = this.getLenderNamesForDisplay();
    if (lenderNames.length > 0) {
      paymentMethodEntity.seedProviderNames(lenderNames);
    }

    this.paymentMethod = paymentMethodEntity;
  }

  protected async setPaymentMethodFromLoan(loan: Loan): Promise<void> {
    const paymentMethodEntity = PaymentMethodFactory.byType(this.type) as AbstractLoanPaymentMethod;

    if (!(paymentMethodEntity instanceof CreditLinePaymentMethod)) {
      throw new Error('Payment method entity is not a credit line payment method');
    }

    let lenderNames: LenderName[] | undefined;

    if (loan.loanApplicationSelected) {
      lenderNames = [loan.loanApplicationSelected.lenderName];
    } else {
      const displayLenderNames = this.getLenderNamesForDisplay();
      lenderNames = displayLenderNames.length > 0 ? displayLenderNames : undefined;
    }

    const totalToFinance = this.totalToFinance();
    paymentMethodEntity.setup(totalToFinance.value, totalToFinance.currency);
    paymentMethodEntity.setConditionFromLoanConditions(loan, lenderNames);

    this.paymentMethod = paymentMethodEntity;
  }

  private getLenderNamesForDisplay(): LenderName[] {
    const processingStrategyLenderNames = this.getLenderNamesFromProcessingStrategy();
    if (processingStrategyLenderNames.length > 0) {
      return processingStrategyLenderNames;
    }

    const providerName = this.getPaymentIntent()?.providerName;
    if (providerName) {
      return [providerName as LenderName];
    }

    return [];
  }

  public clone(): this {
    const clone = new CreditLinePaymentMethodDetailEntity(this.type, this.checkoutSession, this.agent);

    Object.assign(clone, this);

    return clone as this;
  }
}
